import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, CaretRight, MoonStars, WifiSlash } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getMovementHistory } from '../../lib/movement';
import { useNightData, buildSubjects, MAX_OFFSET } from './useNightData';
import { nightWindow } from '../../lib/nightReport';
import { lastNights } from '../../lib/movementView';
import { DayBarLegend } from '../movement/DayBar';
import { fmtInTz } from '../../lib/tz';
import { NightRow } from './NightRow';

export function NightSummary() {
  const { t, lang } = useI18n();
  const { current } = useOrg();
  const { user } = useAuth();
  const base = useNightData(current?.id);
  const [offset, setOffset] = useState(0);
  const [buckets, setBuckets] = useState(null);
  const now = useMemo(() => Date.now(), [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const win = useMemo(() => (base ? nightWindow(base.policy, now, offset) : null), [base, now, offset]);
  const lo = Math.max(0, offset - 6);
  const baseFrom = useMemo(() => (base ? nightWindow(base.policy, now, lo + 6).fromMs : null), [base, now, lo]);
  const baseTo = useMemo(() => (base ? nightWindow(base.policy, now, lo).toMs : null), [base, now, lo]);

  useEffect(() => {
    if (!win) return undefined;
    let alive = true;
    setBuckets(null);
    getMovementHistory({ from: new Date(baseFrom).toISOString(), to: new Date(baseTo).toISOString(), resolution: '5m' })
      .then((b) => alive && setBuckets(b)).catch(() => alive && setBuckets('error'));
    return () => { alive = false; };
  }, [win, baseFrom, baseTo]);

  if (!base || !win) return <div className="text-ink-muted py-10 text-center">{t('loading')}</div>;

  const { subjects, noSensor } = buildSubjects(base, { family: user?.role === 'family' });
  const rows = subjects.map((sub) => ({
    sub, night: Array.isArray(buckets) ? lastNights(buckets, sub.ids, base.policy, now, 7, lo).find((n) => n.offset === offset) : null,
  }));
  const day = (ms) => fmtInTz(ms, win.tz, { weekday: 'short', day: '2-digit', month: 'short' }, lang);

  return (
    <div className="space-y-5" data-testid="night-summary">
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-stone_line rounded-2xl shadow-soft px-5 py-4">
        <div className="flex items-center gap-3">
          <MoonStars size={22} weight="duotone" className="text-sage-600" />
          <div>
            <div className="font-heading text-xl text-ink" data-testid="night-summary-range">{day(win.fromMs)} → {day(win.toMs)}</div>
            <div className="text-xs text-ink-muted">
              {base.policy.night_hours_start.slice(0, 5)}–{base.policy.night_hours_end.slice(0, 5)} · {win.tz}
              {base.policy.fallback && <span className="text-[#8a6224]"> · {t('rep_policy_fallback')}</span>}
            </div>
          </div>
        </div>
        <DayBarLegend night={false} />
        <div className="flex items-center gap-1">
          <button type="button" disabled={offset >= MAX_OFFSET} onClick={() => setOffset(offset + 1)} data-testid="night-summary-prev"
            className="p-2 rounded-lg border border-stone_line text-ink-soft hover:bg-bg-alt disabled:opacity-40"><CaretLeft size={16} /></button>
          <span className="text-xs text-ink-soft px-2 min-w-[90px] text-center" data-testid="night-summary-label">
            {offset === 0 ? t('rep_last_night') : t('rep_nights_ago', { n: offset + 1 })}
          </span>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - 1)} data-testid="night-summary-next"
            className="p-2 rounded-lg border border-stone_line text-ink-soft hover:bg-bg-alt disabled:opacity-40"><CaretRight size={16} /></button>
        </div>
      </div>

      {buckets === 'error' ? (
        <div className="bg-terracotta-50 text-terracotta-700 rounded-xl p-4 text-sm" data-testid="night-summary-error">{t('wt_load_failed')}</div>
      ) : rows.length + noSensor.length === 0 ? (
        <div className="text-ink-muted py-10 text-center">{t('no_results')}</div>
      ) : (
        <ul className="space-y-3" data-testid="night-summary-list">
          {rows.map(({ sub, night }) => (
            <li key={`${sub.kind}-${sub.id}`}>
              <NightRow subject={sub} night={night} buckets={buckets} />
            </li>
          ))}
          {noSensor.map((p) => (
            <li key={p.id}>
              <div className="flex items-center justify-between gap-3 bg-bg-alt/60 border border-stone_line rounded-2xl px-5 py-3" data-testid={`night-row-${p.id}`}>
                <Link to={`/patients/${p.id}`} className="text-sm text-ink-soft hover:text-ink">{p.name}</Link>
                <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted"><WifiSlash size={13} />{t('mv_state_no_sensor')}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ink-muted">{t('rep_night_note')}</p>
    </div>
  );
}
